import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";

const EMPTY = { affiliateId: "", email: "", password: "" };

export default function Promoters() {
  const [items, setItems] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([api.get("/promoters"), api.get("/affiliates")]);
      setItems(p.data);
      setAffiliates(a.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/promoters", form);
      setShowForm(false);
      setForm(EMPTY);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleStatus = async (p) => {
    await api.put(`/promoters/${p.id}`, { status: p.status === "active" ? "inactive" : "active" });
    await load();
  };

  return (
    <div data-testid="promoters-page">
      <header className="page-head">
        <h1>Affiliate Portal Access</h1>
        <button className="btn btn-primary" data-testid="new-promoter-btn" onClick={() => { setForm(EMPTY); setError(""); setShowForm(true); }}>
          + Give portal access
        </button>
      </header>
      <p className="muted">Portal access is optional. It gives an existing affiliate a private login to view their referral link, commissions and payout history.</p>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty" data-testid="promoters-empty">No affiliate portal accounts yet.</div>
      ) : (
        <div className="table-wrap">
          <table data-testid="promoters-table">
            <thead>
              <tr>
                <th>Email</th><th>Affiliate</th><th>Code</th><th>Status</th><th>Totals by currency</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`promoter-row-${p.id}`}>
                  <td className="cell-strong">{p.email}</td>
                  <td>{p.affiliateName || "—"}</td>
                  <td><code>{p.code || "—"}</code></td>
                  <td>
                    <span className={"badge " + (p.status === "active" ? "badge-green" : "badge-gray")}>{p.status}</span>
                    {p.mustChangePassword && <span className="badge badge-yellow" style={{ marginLeft: 6 }}>must change pwd</span>}
                  </td>
                  <td>
                    {(p.summary || []).length === 0 ? "—" : (p.summary || []).map((s) => (
                      <div key={s.currency} className="small">
                        {s.currency}: pending {s.pending} · payable {s.payable} · paid {s.paid} · lifetime {s.lifetime}
                      </div>
                    ))}
                  </td>
                  <td className="actions">
                    <button className="btn btn-ghost btn-sm" data-testid={`view-promoter-${p.id}`} onClick={() => navigate(`/promoters/${p.id}`)}>View</button>
                    <button className="btn btn-ghost btn-sm" data-testid={`toggle-promoter-${p.id}`} onClick={() => toggleStatus(p)}>
                      {p.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="promoter-form">
            <h2>Give Affiliate Portal Access</h2>
            {error && <div className="alert" data-testid="promoter-form-error">{error}</div>}
            <form onSubmit={submit}>
              <label>Affiliate *</label>
              <select data-testid="promoter-affiliate" value={form.affiliateId} required
                onChange={(e) => setForm({ ...form, affiliateId: e.target.value })}>
                <option value="">— select affiliate —</option>
                {affiliates.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
              </select>
              <label>Login email *</label>
              <input type="email" data-testid="promoter-email" value={form.email} required
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <label>Initial password * (min 8 chars)</label>
              <input type="text" data-testid="promoter-password" value={form.password} required
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="promoter-form-submit">Give access</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
